<template>
  <div class="category-nav">
    <div class="category-container">
      <ul class="category-list">
        <li
          v-for="item in categoryList"
          :key="item.Id"
          @click="debouncedCategoryClick(item)"
          :class="{ active: activeId === item.Id }"
        >
          <template v-if="item.Title === '每日签到'">
            <CallWxMiniProgram
              v-if="isWechatEnv"
              username="gh_9318f8f3b6ea"
              path="pages/newSign/newSign"
              :imageUrl="getImageUrl(item.ImageUrl)"
              :title="item.Title"
              imageWidth="70%"
              @launch-success="handleLaunchSuccess"
              @launch-error="handleLaunchError"
              :debug="false"
            />
            <div v-else class="fallback-sign-in" @click="handleSignInFallback">
              <img
                class="category-icon"
                :src="getImageUrl(item.ImageUrl)"
                alt="每日签到"
              />
              <span class="category-title">每日签到</span>
            </div>
          </template>
          <template v-else>
            <img
              class="category-icon"
              :src="getImageUrl(item.ImageUrl)"
              :alt="item.Title"
            />
            <span class="category-title">{{ item.Title }}</span>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { getHomeCategoryList } from "@/api/advertisement";
import { getSpecialActivityDetail } from "@/api/specialActivity.js";
import { debounce } from "lodash-es";
import { nextTick } from 'vue';
import CallWxMiniProgram from './Call-WxMiniProgram.vue';
// URL模式常量配置
const URL_PATTERNS = {
  SPECIAL_ACTIVITY: 'UnUse', // 修改为未使用状态
  SHOP_DETAIL: '/shop/', // 商城详情URL特征
  ACTIVITY_HOME: '/home/', // 活动首页URL特征
}

// 基础域名配置
const BASE_URL = "https://gdtvcms.gdtvdv.com";

export default {
  name: "CategoryNavHome",
  components: {
    CallWxMiniProgram,
  },
  data() {
    return {
      categoryList: [],
      activeId: null,
      urlPatterns: URL_PATTERNS,
      isWechatEnv: false,
      isInMiniProgram: false
    };
  },

  async created() {
    this.checkEnvironment();
    this.fetchCategories();
    this.debouncedCategoryClick = debounce(this.handleCategoryClick, 300);
  },

  methods: {
    checkEnvironment() {
      const ua = navigator.userAgent.toLowerCase();
      this.isWechatEnv = /micromessenger/.test(ua);
      if (window.wx && window.wx.miniProgram) {
        window.wx.miniProgram.getEnv(res => {
          this.isInMiniProgram = res.miniprogram;
        });
      }
    },

    async fetchCategories() {
      try {
        const res = await getHomeCategoryList();
        if (res.ErrorCode === 0) {
          this.categoryList = res.Data.sort((a, b) => a.Sort - b.Sort);
        }
      } catch (error) {
        console.error("获取分类列表失败:", error);
      }
    },

    getImageUrl(imageUrlStr) {
      try {
        const imageData = JSON.parse(imageUrlStr)[0];
        return imageData.ServerUrl + imageData.FilePath;
      } catch (error) {
        return "";
      }
    },

    /**
     * URL清理和验证
     * @param {string} url - 需要处理的URL
     * @returns {string} - 处理后的URL或空字符串
     */
    sanitizeUrl(url) {
      if (!url) return "";

      // 移除前后空格
      url = url.trim();

      try {
        // 如果是相对路径，添加基础域名
        if (url.startsWith("/")) {
          url = `${BASE_URL}${url}`;
        }

        // 验证URL格式是否合法
        new URL(url);
        return url;
      } catch (error) {
        console.error("非法URL格式:", url);
        return "";
      }
    },

    /**
     * 分类对象基础验证
     * @param {Object} category - 分类对象
     * @returns {boolean} - 验证结果
     */
    validateCategory(category) {
      // 验证分类对象是否存在
      if (!category || typeof category !== "object") {
        console.error("无效的分类对象");
        return false;
      }

      // 验证基础必需属性
      const requiredProps = {
        Id: "分类ID",
        Title: "分类标题",
      };

      for (const [prop, desc] of Object.entries(requiredProps)) {
        if (!category[prop]) {
          console.error(`缺少${desc}`);
          return false;
        }
      }

      // ID必须为数字类型
      if (typeof category.Id !== "number") {
        console.error("分类ID必须为数字类型");
        return false;
      }

      // Title必须为字符串类型
      if (typeof category.Title !== "string") {
        console.error("分类标题必须为字符串类型");
        return false;
      }

      return true;
    },

    /**
     * 专区活动URL验证
     * @param {string} url - 专区活动URL
     * @returns {boolean} - 验证结果
     */
    validateSpecialActivityUrl(url) {
      const match = url.match(/\/home\/(\d+)$/);
      if (!match || !match[1]) {
        console.error("无效的专区活动URL格式");
        return false;
      }
      return true;
    },

    /**
     * 商城详情URL验证
     * @param {string} url - 商城详情URL
     * @returns {boolean} - 验证结果
     */
    validateShopDetailUrl(url) {
      const hashPart = url.split("#")[1];
      if (!hashPart) {
        console.error("无效的商城详情URL格式: 缺少hash部分");
        return false;
      }

      const queryPart = hashPart.split("?")[1];
      if (!queryPart) {
        console.error("无效的商城详情URL格式: 缺少查询参数");
        return false;
      }

      return true;
    },

    /**
     * 分类点击处理主函数
     * @param {Object} category - 分类对象
     */
    async handleCategoryClick(category) {
      // 基础验证
      if (!this.validateCategory(category)) {
        return;
      }

      this.activeId = category.Id;

      // 处理每日签到按钮点击
      if (category.Title === '每日签到') {
        // CallWxMiniProgram 组件已处理微信环境下的跳转，这里无需额外逻辑
        // 非微信环境下的回退逻辑由 fallback-sign-in 的点击事件处理
        return; 
      }
      
      // 处理其他分类按钮点击
      const cleanUrl = this.sanitizeUrl(category.Url);
      if (!cleanUrl) {
        this.emitCategoryChange(category);
        return;
      }

      try {
        if (await this.handleSpecialActivityUrl(cleanUrl)) {
          return;
        }
        
        if (await this.handleShopDetailUrl(cleanUrl)) {
          return;
        }
      } catch (error) {
        console.error('URL解析失败:', error);
      }

      this.emitCategoryChange(category);
    },

    /**
     * 处理商城详情URL
     * @param {string} url - URL字符串
     * @returns {boolean} - 处理结果
     */
    async handleShopDetailUrl(url) {
      try {
        // 1. 解析URL
        const urlObj = new URL(url)
        
        // 2. 处理第一种URL格式: /shop/index.html#/second?参数
        if (urlObj.hash && urlObj.hash.includes('/second?')) {
          const queryString = urlObj.hash.split('?')[1]
          if (!queryString) return false
          
          const searchParams = new URLSearchParams(queryString)
          const routeQuery = {
            bannerCode: searchParams.get('as') ? `${searchParams.get('as')}_BANNER2023` : undefined,
            navCode: searchParams.get('code') || undefined,
            nineCode: searchParams.get('ninecode') || undefined
          }

          // 移除undefined的属性
          Object.keys(routeQuery).forEach(key => 
            routeQuery[key] === undefined && delete routeQuery[key]
          )

          // 必须有 navCode
          if (!routeQuery.navCode) return false

          await this.$router.push({
            name: 'CategoryDetail',
            query: routeQuery
          })
          return true
        }
        
        // 3. 处理第二种URL格式: /180312specialactivity/#!/home/数字
        if (url.includes('specialactivity') && url.includes('/home/')) {
          const match = url.match(/\/home\/(\d+)$/)
          if (!match || !match[1]) return false
          
          await this.$router.push({
            name: 'CategoryDetail',
            query: {
              navCode: match[1]
            }
          })
          return true
        }

        return false
      } catch (error) {
        console.error('URL处理失败:', error)
        return false
      }
    },

    // 保留原有的 handleSpecialActivityUrl 方法，但实不会被使用
    async handleSpecialActivityUrl(url) {
      if (!url.includes(this.urlPatterns.SPECIAL_ACTIVITY)) return false
      return false
    },

    // 新增：处理签到按钮在非微信环境下的回退点击事件
    handleSignInFallback() {
      this.$toast ? 
        this.$toast('请在微信中打开进行签到') : 
        alert('请在微信中打开进行签到');
    },

    // 新增：处理小程序跳转成功的回调 (可选)
    handleLaunchSuccess(e) {
      console.log('小程序跳转成功:', e);
    },

    // 新增：处理小程序跳转失败的回调 (可选)
    handleLaunchError(e) {
      console.error('小程序跳转失败:', e);
      this.$toast ? 
        this.$toast('跳转签到小程序失败，请稍后重试') : 
        alert('跳转签到小程序失败，请稍后重试');
    },

    // 统一处理类别变更事件
    emitCategoryChange(category) {
      this.$emit("category-change", {
        id: category.Id,
        title: category.Title,
      });
    },
  },
};
</script>

<style scoped>
.category-nav {
  width: 100%;
  padding: 1.5vh 0;
  background: #fff;
}

.category-container {
  margin: 0 auto;
  padding: 0 0.6rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.category-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.category-list li {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.3rem;
  background: #fff7e6;
  border-radius: 0.4rem;
  aspect-ratio: 1;
}

.category-icon {
  width: 70%;
  height: auto;
  aspect-ratio: 1;
  margin-bottom: 0.2rem;
  object-fit: contain;
}

.category-title {
  font-size: 0.7rem;
  color: #333;
  text-align: center;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  padding: 0 0.1rem;
}

.category-list li.active .category-title {
  color: #1890ff;
}

/* 中等屏幕优化 */
@media screen and (min-width: 375px) {
  .category-container {
    padding: 0 0.8rem;
  }

  .category-list {
    gap: 0.6rem;
  }

  .category-list li {
    padding: 0.4rem;
  }

  .category-title {
    font-size: 0.75rem;
  }
}

/* 大屏幕优化 */
@media screen and (min-width: 768px) {
  .category-container {
    padding: 0 1.2rem;
    max-width: 1200px;
  }

  .category-list {
    gap: 1rem;
  }

  .category-list li {
    padding: 0.6rem;
    border-radius: 0.6rem;
  }

  .category-icon {
    width: 75%;
  }

  .category-title {
    font-size: 0.85rem;
  }
}

:deep(wx-open-launch-weapp) {
  display: block;
  width: 100%;
  height: 100%;
}

/* 确保签到按钮内容在 wx-open-launch-weapp 中正确显示 */
:deep(wx-open-launch-weapp div[slot="launch-btn"]) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* 添加回退签到按钮的样式 */
.fallback-sign-in {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
</style>
